import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, Divider, FormHelperText, IconButton, Stack } from '@mui/material';
import TextField from '@mui/material/TextField';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  cleanOptions,
  emptyAnalyticsFieldRow,
  hasRepeatedOptions,
  isBlankRow,
  isNameUsedTwice,
  isRangeValid,
  MAX_ANALYTICS_FIELDS,
  MAX_FIELD_DESCRIPTION_LENGTH,
  MAX_FIELD_NAME_LENGTH,
  MAX_FIELD_OPTION_LENGTH,
  MAX_FIELD_OPTIONS,
  MAX_SUMMARY_INSTRUCTION_LENGTH,
  MAX_TOTAL_FIELD_OPTIONS,
  MIN_FIELD_OPTIONS,
  totalOptions,
} from '../../../helpers/analyticsFields';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentFormData } from '../../../interfaces/Agent';
import StyledFormItem from '../../../styles/StyledFormItem';
import AutocompleteInput from '../../shared/AutoCompleteInput/AutoCompleteInput';
import Button from '../../shared/Button/Button';
import DropDown from '../../shared/DropDown/DropDown';
import Input from '../../shared/Input/Input';
import Text from '../../shared/Text/Text';
import ToolTip from '../../shared/ToolTip/ToolTip';

const whiteInput = { '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } };

const sectionTitleSx = {
  fontSize: '1.25rem !important',
  '@media (min-width: 769px)': { fontSize: '1.25rem !important' },
  '@media (min-width: 1441px)': { fontSize: '1.25rem !important' },
  fontWeight: '600 !important',
  color: 'var(--page-sub-header)',
};

type RangeEnd = 'range_low' | 'range_high';

const AnalyticsFieldsSection: React.FC = () => {
  const { t } = useTranslation();
  const {
    control,
    watch,
    getValues,
    formState: { errors, isSubmitted },
  } = useFormContext<AgentFormData>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'analytics_fields',
  });
  const rows = watch('analytics_fields') ?? [];
  const fieldErrors = errors.analytics_fields;
  const tooManyOptions = totalOptions(rows) > MAX_TOTAL_FIELD_OPTIONS;

  const kindOptions = [
    { label: t('Text'), value: 'text' },
    { label: t('Yes / No'), value: 'yes_no' },
    { label: t('Analytics field type number'), value: 'number' },
    { label: t('Choice'), value: 'choice' },
  ];

  const rangeRule = (end: RangeEnd, index: number) => {
    const other: RangeEnd = end === 'range_low' ? 'range_high' : 'range_low';
    return {
      deps: [`analytics_fields.${index}.${other}` as const],
      validate: (value: string) => {
        const otherValue = getValues(`analytics_fields.${index}.${other}`);
        const [low, high] =
          end === 'range_low' ? [value, otherValue] : [otherValue, value];
        return (
          isRangeValid(low, high) ||
          t('The lowest value must be smaller than the highest')
        );
      },
    };
  };

  return (
    <>
      <Box sx={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Text type="subtitle1" sx={sectionTitleSx}>
            {t('Analytics Fields')}
          </Text>
          <ToolTip
            title={t(
              'Information the AI fills in from each call after it ends. Not the same as Data to collect, which the agent asks for during the call.'
            )}
          />
        </Stack>
      </Box>

      {fields.map((field, index) => {
        const row = rows[index];
        // Half-filled rows show their errors once a save was tried. Empty rows are dropped on save.
        const showErrors = isSubmitted && !!row && !isBlankRow(row);
        const nameMissing = showErrors && !row.name?.trim();
        const nameUsedTwice = isNameUsedTwice(rows, index);
        const descriptionMissing = showErrors && !row.description?.trim();
        const tooFewOptions =
          showErrors &&
          row.kind === 'choice' &&
          cleanOptions(row).length < MIN_FIELD_OPTIONS;
        const repeatedOptions =
          row?.kind === 'choice' && hasRepeatedOptions(row);
        const rangeError =
          fieldErrors?.[index]?.range_low?.message ||
          fieldErrors?.[index]?.range_high?.message;

        return (
          <Box key={field.id} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Divider
              sx={{
                marginTop: '0.75rem',
                fontSize: '0.8rem',
                color: 'var(--page-sub-header)',
                '&::before, &::after': {
                  borderColor: 'rgba(131, 131, 131, 0.3)',
                },
              }}
            >
              {index + 1}
            </Divider>
            <Box
              sx={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}
            >
              <StyledFormItem flexGrow={1} width="auto">
                <Text type="label">{t('Field Name')}</Text>
                <Input
                  name={`analytics_fields.${index}.name`}
                  maxLength={MAX_FIELD_NAME_LENGTH}
                  placeholder={t('satisfaction')}
                  inputProps={{ 'aria-label': t('Field Name') }}
                  withError={nameMissing || nameUsedTwice}
                  helperText={
                    nameUsedTwice
                      ? t('This name is already used')
                      : t('Field name is required')
                  }
                  sx={whiteInput}
                />
              </StyledFormItem>

              <StyledFormItem flexGrow={1} width="auto">
                <Text type="label">{t('Type')}</Text>
                <DropDown
                  name={`analytics_fields.${index}.kind`}
                  options={kindOptions}
                  required={true}
                  fullWidth={true}
                  ariaLabel={t('Type')}
                />
              </StyledFormItem>

              <IconButton
                color="error"
                aria-label={t('Remove field')}
                sx={{
                  height: '56px',
                  width: '56px',
                  marginTop: '70px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid lightgrey',
                  flexShrink: 0,
                }}
                type="button"
                onClick={() => remove(index)}
              >
                <DeleteOutline />
              </IconButton>
            </Box>

            <StyledFormItem>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Text type="label">
                  {t('Field Description')}
                  <ToolTip
                    title={t('What the AI should look for in the call')}
                  />
                </Text>
              </Stack>
              <Input
                name={`analytics_fields.${index}.description`}
                maxLength={MAX_FIELD_DESCRIPTION_LENGTH}
                multiline
                inputProps={{
                  style: { minHeight: '60px' },
                  'aria-label': t('Field Description'),
                }}
                placeholder={t(
                  'How satisfied the candidate is with their current job'
                )}
                withError={descriptionMissing}
                helperText={t('Description is required')}
                sx={whiteInput}
              />
            </StyledFormItem>

            {row?.kind === 'choice' && (
              <StyledFormItem>
                <AutocompleteInput
                  name={`analytics_fields.${index}.options`}
                  label={t('Choices')}
                  options={[]}
                  maxItems={MAX_FIELD_OPTIONS}
                  maxItemLength={MAX_FIELD_OPTION_LENGTH}
                  autoSelect
                  placeholder={t('Type a choice and press Enter')}
                />
                {tooFewOptions && (
                  <FormHelperText error>
                    {t('At least 2 choices are needed')}
                  </FormHelperText>
                )}
                {repeatedOptions && (
                  <FormHelperText error>
                    {t('The same choice is listed twice')}
                  </FormHelperText>
                )}
              </StyledFormItem>
            )}

            {row?.kind === 'number' && (
              <StyledFormItem>
                <Text type="label">
                  {t('Range')}
                  <ToolTip
                    title={t(
                      'Optional. Answers outside the range are left empty.'
                    )}
                  />
                </Text>
                <Stack direction="row" gap="1rem">
                  {(['range_low', 'range_high'] as const).map(end => (
                    <Controller
                      key={end}
                      name={`analytics_fields.${index}.${end}`}
                      control={control}
                      rules={rangeRule(end, index)}
                      render={({ field: rangeField }) => (
                        <TextField
                          type="number"
                          fullWidth
                          value={rangeField.value ?? ''}
                          onChange={rangeField.onChange}
                          onBlur={rangeField.onBlur}
                          error={!!rangeError}
                          placeholder={
                            end === 'range_low'
                              ? t('Lowest value')
                              : t('Highest value')
                          }
                          inputProps={{
                            'aria-label':
                              end === 'range_low'
                                ? t('Lowest value')
                                : t('Highest value'),
                          }}
                          sx={whiteInput}
                        />
                      )}
                    />
                  ))}
                </Stack>
                {rangeError && (
                  <FormHelperText error>{rangeError}</FormHelperText>
                )}
              </StyledFormItem>
            )}
          </Box>
        );
      })}

      {tooManyOptions && (
        <FormHelperText error>
          {t('All choice fields together can have at most {{number}} choices', {
            number: MAX_TOTAL_FIELD_OPTIONS,
          })}
        </FormHelperText>
      )}

      <Box sx={{ marginTop: '1rem' }}>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          size="small"
          onClick={() => append(emptyAnalyticsFieldRow())}
          disabled={fields.length >= MAX_ANALYTICS_FIELDS}
          style={{ backgroundColor: '#fff' }}
        >
          <Add sx={{ marginRight: '0.5rem' }} />
          {t('Add field')}
        </Button>
      </Box>

      <Box sx={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Text type="subtitle1" sx={sectionTitleSx}>
            {t('Summary Instruction')}
          </Text>
          <ToolTip
            title={t(
              'How the call summary should look: what to put in it, the layout and the length. Leave it empty for the default short summary.'
            )}
          />
        </Stack>
      </Box>
      <StyledFormItem>
        <Input
          name="summary_instruction"
          maxLength={MAX_SUMMARY_INSTRUCTION_LENGTH}
          multiline
          inputProps={{
            style: { minHeight: '100px' },
            'aria-label': t('Summary Instruction'),
          }}
          placeholder={t(
            'Write a note with these parts: Candidate, Current job, Salary. Facts only.'
          )}
          sx={whiteInput}
        />
      </StyledFormItem>
    </>
  );
};

export default AnalyticsFieldsSection;
